import { graphql } from '~/graphql';
import type { PriceNegotiationStatus } from '~/graphql/graphql';
import { useGraphqlMutation } from '~/hooks/useGraphqlMutation';
import { useGraphqlQuery } from '~/hooks/useGraphqlQuery';
import type {
  PaginatedFetchOptions,
  RouteSort,
} from '~/hooks/usePaginatedQuery';

export const usePendingPriceNegotiations = (platformProductId: string) => {
  const query = graphql(`
    query PendingPriceNegotiations($platformProductId: ID!) {
      pendingPriceNegotiations(platformProductId: $platformProductId) {
        id
        platformProductLength {
          id
        }
        platformProductCannedTally {
          id
        }
        sellingOrganization {
          id
        }
      }
    }
  `);

  return useGraphqlQuery(
    query,
    ['pendingPriceNegotiations', platformProductId],
    { platformProductId }
  );
};

export const useAcceptedPriceNegotiations = (platformProductId: string) => {
  const query = graphql(`
    query AcceptedPriceNegotiations($platformProductId: ID!) {
      acceptedPriceNegotiations(platformProductId: $platformProductId) {
        id
        platformProductCannedTally {
          id
        }
        sellingOrganization {
          id
        }
        unit
        offeredPrice {
          amount
          currency
        }
      }
    }
  `);

  return useGraphqlQuery(
    query,
    ['acceptedPriceNegotiations', platformProductId],
    { platformProductId }
  );
};

export type SellerPriceNegotiationFilters = {
  assignedToMe?: boolean;
  status?: PriceNegotiationStatus[];
};

export const useSellerPriceNegotiations = (
  page: number,
  size: number,
  filters?: SellerPriceNegotiationFilters,
  sort?: RouteSort,
  options?: PaginatedFetchOptions
) => {
  const query = graphql(`
    query SellerPriceNegotiations(
      $page: Int!
      $size: Int!
      $assignedToMe: Boolean
      $status: [PriceNegotiationStatus!]
    ) {
      sellerPriceNegotiations(
        pageInput: { page: $page, size: $size }
        assignedToMe: $assignedToMe
        status: $status
      ) {
        paging {
          totalElements
          totalPages
          number
          size
        }
        content {
          id
          status
          unit
          targetPrice {
            amount
            currency
          }
          offeredPrice {
            amount
            currency
          }
          buyerNote
          sellerNote
          createdAt
          updatedAt
          buyingOrganization {
            id
            name
          }
          sellingOrganization {
            id
            name
            sellingOrganizationCode
          }
          createdBy {
            id
            firstName
            lastName
          }
          platformProductLength {
            id
            lengthDisplayValue
            isNominal
            platformProduct {
              id
              name
              code
            }
          }
          platformProductCannedTally {
            id
            name
            platformProduct {
              id
              code
            }
          }
          recipients {
            id
            firstName
            lastName
          }
        }
      }
    }
  `);

  return useGraphqlQuery(
    query,
    [
      'sellerPriceNegotiations',
      page.toString(),
      size.toString(),
      filters?.assignedToMe ?? false,
      filters?.status ?? 'ALL',
      sort ?? null,
    ],
    {
      page,
      size,
      assignedToMe: filters?.assignedToMe,
      status: filters?.status,
    },
    options
  );
};

export const useSellerPriceNegotiationDetail = (
  id: string,
  options?: { enabled?: boolean }
) => {
  const query = graphql(`
    query SellerPriceNegotiationDetail($id: ID!) {
      sellerPriceNegotiation(id: $id) {
        id
        tallyRecipe {
          lengthLabel
          bundleCount
          piecesPerBundle
        }
        currentSellerListPrice {
          amount
          currency
        }
        offerCurrency
      }
    }
  `);

  return useGraphqlQuery(
    query,
    ['sellerPriceNegotiationDetail', id],
    { id },
    options
  );
};

export const useSellerPriceNegotiationCounts = (
  assignedToMe?: boolean,
  options?: { enabled?: boolean }
) => {
  const query = graphql(`
    query SellerPriceNegotiationCounts($assignedToMe: Boolean) {
      sellerPriceNegotiationCounts(assignedToMe: $assignedToMe) {
        waiting
        responded
      }
    }
  `);

  return useGraphqlQuery(
    query,
    ['sellerPriceNegotiationCounts', assignedToMe ?? false],
    { assignedToMe },
    options
  );
};

export type BuyerPriceNegotiationFilters = {
  status?: PriceNegotiationStatus[];
};

export const useBuyerPriceNegotiations = (
  page: number,
  size: number,
  filters?: BuyerPriceNegotiationFilters,
  sort?: RouteSort,
  options?: PaginatedFetchOptions
) => {
  const query = graphql(`
    query BuyerPriceNegotiations(
      $page: Int!
      $size: Int!
      $status: [PriceNegotiationStatus!]
    ) {
      buyerPriceNegotiations(
        pageInput: { page: $page, size: $size }
        status: $status
      ) {
        paging {
          totalElements
          totalPages
          number
          size
        }
        content {
          id
          status
          unit
          targetPrice {
            amount
            currency
          }
          offeredPrice {
            amount
            currency
          }
          buyerNote
          sellerNote
          createdAt
          updatedAt
          buyingOrganization {
            id
            name
          }
          sellingOrganization {
            id
            name
            sellingOrganizationCode
          }
          createdBy {
            id
            firstName
            lastName
          }
          recipients {
            id
            firstName
            lastName
          }
          platformProductLength {
            id
            lengthDisplayValue
            isNominal
            platformProduct {
              id
              name
              code
            }
          }
          platformProductCannedTally {
            id
            name
            platformProduct {
              id
              code
            }
          }
        }
      }
    }
  `);

  return useGraphqlQuery(
    query,
    [
      'buyerPriceNegotiations',
      page.toString(),
      size.toString(),
      filters?.status ?? 'ALL',
      sort ?? null,
    ],
    { page, size, status: filters?.status },
    options
  );
};

export const useBuyerPriceNegotiationCounts = (options?: {
  enabled?: boolean;
}) => {
  const query = graphql(`
    query BuyerPriceNegotiationCounts {
      buyerPriceNegotiationCounts {
        waiting
        responded
      }
    }
  `);

  return useGraphqlQuery(
    query,
    ['buyerPriceNegotiationCounts'],
    undefined,
    options
  );
};

export const useRequestPriceNegotiation = () => {
  const mutation = graphql(`
    mutation RequestPriceNegotiation(
      $platformProductLengthIds: [ID!]!
      $sellingOrganizationIds: [ID!]!
      $targetPrice: MoneyInput
      $buyerNote: String
      $recipientUserIds: [ID!]
    ) {
      requestPriceNegotiation(
        platformProductLengthIds: $platformProductLengthIds
        sellingOrganizationIds: $sellingOrganizationIds
        targetPrice: $targetPrice
        buyerNote: $buyerNote
        recipientUserIds: $recipientUserIds
      )
    }
  `);

  return useGraphqlMutation(mutation, ['pendingPriceNegotiations']);
};

export const useRespondToPriceNegotiationInApp = () => {
  const mutation = graphql(`
    mutation RespondToPriceNegotiationInApp(
      $id: ID!
      $input: RespondToPriceNegotiationInput!
    ) {
      respondToPriceNegotiationInApp(id: $id, input: $input) {
        id
        status
      }
    }
  `);

  return useGraphqlMutation(mutation, [
    'sellerPriceNegotiations',
    'sellerPriceNegotiationCounts',
  ]);
};

export const useDeclinePriceNegotiationRequest = () => {
  const mutation = graphql(`
    mutation DeclinePriceNegotiationRequest($id: ID!, $reason: String) {
      declinePriceNegotiationRequest(id: $id, reason: $reason) {
        id
        status
      }
    }
  `);

  return useGraphqlMutation(mutation, [
    'sellerPriceNegotiations',
    'sellerPriceNegotiationCounts',
  ]);
};

export const useRequestTallyPriceNegotiation = () => {
  const mutation = graphql(`
    mutation RequestTallyPriceNegotiation(
      $platformProductCannedTallyIds: [ID!]!
      $sellingOrganizationIds: [ID!]!
      $unit: UnitCode!
      $targetPrice: MoneyInput
      $buyerNote: String
      $recipientUserIds: [ID!]
    ) {
      requestTallyPriceNegotiation(
        platformProductCannedTallyIds: $platformProductCannedTallyIds
        sellingOrganizationIds: $sellingOrganizationIds
        unit: $unit
        targetPrice: $targetPrice
        buyerNote: $buyerNote
        recipientUserIds: $recipientUserIds
      )
    }
  `);

  return useGraphqlMutation(mutation, ['pendingPriceNegotiations']);
};
